import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  Alert,
  CircularProgress,
  Tooltip,
} from '@mui/material';

import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  Delete as DeleteIcon,
  Info as InfoIcon,
  LockOpen as LockOpenIcon
} from '@mui/icons-material';

// import { DataGrid } from '@mui/x-data-grid';

import apiService from '../services/api';
import MUIDataTable from "mui-datatables";
import { createTheme, ThemeProvider } from '@mui/material/styles';

const UsersPage = () => {
  const navigate = useNavigate();

  // Состояние для данных
  const [users, setUsers] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  // Состояние для диалога блокировки
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Состояние для диалога удаления
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Состояние для уведомлений
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Загрузка данных
  const fetchUsers = async () => {
    try {
      setLoading(true);
      // const response = await apiService.users.getAll(page + 1, pageSize, search);
      const response = await apiService.users.getAll(1, 0);

      if (response.data.success) {
        setUsers(response.data.data.users);
        setTotalUsers(response.data.data.total);
      }
    } catch (error) {
      console.error('Ошибка загрузки пользователей:', error);
      setSnackbar({
        open: true,
        message: 'Не удалось загрузить список пользователей',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Загрузка данных при изменении параметров
  useEffect(() => {
    fetchUsers();
  }, [page, pageSize]);

  // Обработчик поиска
  const handleSearch = () => {
    setPage(0);
    fetchUsers();
  };

  // Обработчик очистки поиска
  const handleClearSearch = () => {
    setSearch('');
    setPage(0);
    fetchUsers();
  };

  // Обработчик нажатия Enter в поле поиска
  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Обработчик открытия диалога блокировки
  const handleOpenBlockDialog = (user) => {
    setSelectedUser(user);
    setDialogOpen(true);
  };

  // Обработчик закрытия диалога блокировки
  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedUser(null);
  };

  // Обработчик блокировки/разблокировки пользователя
  const handleToggleBlock = async () => {
    if (!selectedUser) return;

    try {
      setActionLoading(true);
      const response = await apiService.users.toggleBlock(selectedUser._id);

      if (response.data.success) {
        // Обновляем пользователя в списке
        setUsers(users.map(user =>
          user._id === selectedUser._id
            ? { ...user, isBlocked: !user.isBlocked }
            : user
        ));

        // Показываем уведомление
        setSnackbar({
          open: true,
          message: response.data.message,
          severity: 'success'
        });
      }
    } catch (error) {
      console.error('Ошибка блокировки/разблокировки пользователя:', error);
      setSnackbar({
        open: true,
        message: 'Не удалось выполнить операцию',
        severity: 'error'
      });
    } finally {
      setActionLoading(false);
      handleCloseDialog();
    }
  };

  // Обработчик открытия диалога удаления
  const handleOpenDeleteDialog = (user) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  // Обработчик закрытия диалога удаления
  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setUserToDelete(null);
  };

  // Обработчик удаления пользователя
  const handleDeleteUser = async () => {
    if (!userToDelete) return;

    try {
      setDeleteLoading(true);
      const response = await apiService.users.deleteUser(userToDelete._id);

      if (response.data.success) {
        // Удаляем пользователя из списка
        setUsers(users.filter(user => user._id !== userToDelete._id));

        // Показываем уведомление
        setSnackbar({
          open: true,
          message: 'Пользователь успешно удален',
          severity: 'success'
        });

        // Обновляем общее количество
        setTotalUsers(prev => prev - 1);
      }
    } catch (error) {
      console.error('Ошибка удаления пользователя:', error);
      setSnackbar({
        open: true,
        message: 'Не удалось удалить пользователя',
        severity: 'error'
      });
    } finally {
      setDeleteLoading(false);
      handleCloseDeleteDialog();
    }
  };

  // Обработчик закрытия уведомления
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Колонки для таблицы
  // const columns = [
  //   { 
  //     field: 'contact', 
  //     headerName: 'Контакт', 
  //     flex: 1,
  //     minWidth: 180,
  //     renderCell: (params) => (
  //       <Box sx={{ display: 'flex', alignItems: 'center' }}>
  //         {params.row.authMethod === 'email' ? (
  //           <EmailIcon color="info" sx={{ mr: 1 }} />
  //         ) : (
  //           <PhoneIcon color="warning" sx={{ mr: 1 }} />
  //         )}
  //         <Typography>{params.row.email || params.row.phone}</Typography>
  //       </Box>
  //     )
  //   },
  //   { 
  //     field: 'name', 
  //     headerName: 'Имя', 
  //     width: 160,
  //     valueGetter: (params) => params.row.name || 'Не указано'
  //   },
  //   { 
  //     field: 'city', 
  //     headerName: 'Город', 
  //     width: 160,
  //     valueGetter: (params) => params.row.city || 'Не указан'
  //   },
  //   { 
  //     field: 'authMethod', 
  //     headerName: 'Метод', 
  //     width: 100,
  //     renderCell: (params) => (
  //       <Chip 
  //         label={params.value === 'email' ? 'Email' : 'Телефон'} 
  //         color={params.value === 'email' ? 'info' : 'warning'} 
  //         size="small" 
  //         variant="outlined"
  //       />
  //     )
  //   },
  //   { 
  //     field: 'isBlocked', 
  //     headerName: 'Статус', 
  //     width: 120,
  //     renderCell: (params) => (
  //       <Chip 
  //         icon={params.value ? <BlockIcon /> : <CheckCircleIcon />}
  //         label={params.value ? 'Заблокирован' : 'Активен'} 
  //         color={params.value ? 'error' : 'success'} 
  //         size="small" 
  //         variant="outlined"
  //       />
  //     )
  //   },
  //   { 
  //     field: 'createdAt', 
  //     headerName: 'Дата регистрации', 
  //     width: 180,
  //     valueFormatter: (params) => new Date(params.value).toLocaleDateString()
  //   },
  //   { 
  //     field: 'lastLogin', 
  //     headerName: 'Последний вход', 
  //     width: 180,
  //     valueFormatter: (params) => params.value ? new Date(params.value).toLocaleDateString() : 'Нет данных'
  //   },
  //   {
  //     field: 'actions',
  //     headerName: 'Действия',
  //     width: 160,
  //     sortable: false,
  //     renderCell: (params) => (
  //       <Box sx={{ 
  //         display: 'flex', 
  //         flexDirection: 'row',
  //         justifyContent: 'space-around',
  //         gap: 1,
  //         width: '100%'
  //       }}>
  //         <IconButton
  //           size="small"
  //           color={params.row.isBlocked ? 'success' : 'error'}
  //           onClick={() => handleOpenBlockDialog(params.row)}
  //           title={params.row.isBlocked ? 'Разблокировать' : 'Заблокировать'}
  //         >
  //           {params.row.isBlocked ? <LockOpenIcon fontSize="small" /> : <BlockIcon fontSize="small" />}
  //         </IconButton>
  //         <IconButton
  //           size="small"
  //           color="primary"
  //           onClick={() => navigate(`/users/${params.row._id}`)}
  //           title="Детали"
  //         >
  //           <InfoIcon fontSize="small" />
  //         </IconButton>
  //         <IconButton
  //           size="small"
  //           color="error"
  //           onClick={() => handleOpenDeleteDialog(params.row)}
  //           title="Удалить"
  //         >
  //           <DeleteIcon fontSize="small" />
  //         </IconButton>
  //       </Box>
  //     )
  //   }
  // ];

  // const columns = [
  //   {
  //     field: 'contact',
  //     headerName: 'Контакт',
  //     flex: 1.5,
  //     minWidth: 150,
  //     renderCell: (params) => (
  //       <Box sx={{
  //         display: 'flex',
  //         alignItems: 'center',
  //         overflow: 'hidden',
  //         textOverflow: 'ellipsis',
  //         whiteSpace: 'nowrap'
  //       }}>
  //         {params.row.authMethod === 'email' ? (
  //           <EmailIcon color="info" sx={{ mr: 1, flexShrink: 0 }} />
  //         ) : (
  //           <PhoneIcon color="warning" sx={{ mr: 1, flexShrink: 0 }} />
  //         )}
  //         <Typography
  //           noWrap
  //           sx={{
  //             overflow: 'hidden',
  //             textOverflow: 'ellipsis',
  //             fontSize: '0.9rem'
  //           }}
  //         >
  //           {params.row.email || params.row.phone}
  //         </Typography>
  //       </Box>
  //     )
  //   },
  //   {
  //     field: 'name',
  //     headerName: 'Имя',
  //     flex: 1,
  //     minWidth: 100,
  //     valueGetter: (params) => params.row.name || 'Не указано',
  //     renderCell: (params) => (
  //       <Typography
  //         noWrap
  //         sx={{
  //           overflow: 'hidden',
  //           textOverflow: 'ellipsis'
  //         }}
  //       >
  //         {params.value}
  //       </Typography>
  //     )
  //   },
  //   {
  //     field: 'city',
  //     headerName: 'Город',
  //     flex: 1,
  //     minWidth: 100,
  //     valueGetter: (params) => params.row.city || 'Не указан',
  //     renderCell: (params) => (
  //       <Typography
  //         noWrap
  //         sx={{
  //           overflow: 'hidden',
  //           textOverflow: 'ellipsis'
  //         }}
  //       >
  //         {params.value}
  //       </Typography>
  //     )
  //   },
  //   {
  //     field: 'authMethod',
  //     headerName: 'Метод',
  //     flex: 0.8,
  //     minWidth: 90,
  //     align: 'center',
  //     renderCell: (params) => (
  //       <Box sx={{
  //         display: 'flex',
  //         justifyContent: 'center',
  //         width: '100%'
  //       }}>
  //         <Chip
  //           label={params.value === 'email' ? 'Email' : 'Телефон'}
  //           color={params.value === 'email' ? 'info' : 'warning'}
  //           size="small"
  //           variant="outlined"
  //         />
  //       </Box>
  //     )
  //   },
  //   {
  //     field: 'isBlocked',
  //     headerName: 'Статус',
  //     flex: 0.8,
  //     minWidth: 100,
  //     align: 'center',
  //     renderCell: (params) => (
  //       <Box sx={{
  //         display: 'flex',
  //         justifyContent: 'center',
  //         width: '100%'
  //       }}>
  //         <Chip
  //           icon={params.value ? <BlockIcon /> : <CheckCircleIcon />}
  //           label={params.value ? 'Заблокирован' : 'Активен'}
  //           color={params.value ? 'error' : 'success'}
  //           size="small"
  //           variant="outlined"
  //         />
  //       </Box>
  //     )
  //   },
  //   {
  //     field: 'createdAt',
  //     headerName: 'Дата регистрации',
  //     flex: 1,
  //     minWidth: 120,
  //     align: 'center',
  //     // Убираем valueFormatter, чтобы форматировать напрямую в renderCell
  //     renderCell: (params) => {
  //       const date = new Date(params.row.createdAt);
  //       const formattedDate = isNaN(date.getTime()) ? 'Нет данных' : date.toLocaleDateString();
  //       return (
  //         <Typography
  //           noWrap
  //           sx={{
  //             overflow: 'hidden',
  //             textOverflow: 'ellipsis'
  //           }}
  //         >
  //           {formattedDate}
  //         </Typography>
  //       );
  //     }
  //   },
  //   {
  //     field: 'lastLogin',
  //     headerName: 'Последний вход',
  //     flex: 1,
  //     minWidth: 120,
  //     align: 'center',
  //     renderCell: (params) => {
  //       const date = new Date(params.row.lastLogin);
  //       const formattedDate = isNaN(date.getTime()) || !params.row.lastLogin ? 'Нет данных' : date.toLocaleDateString();
  //       return (
  //         <Typography
  //           noWrap
  //           sx={{
  //             overflow: 'hidden',
  //             textOverflow: 'ellipsis'
  //           }}
  //         >
  //           {formattedDate}
  //         </Typography>
  //       );
  //     }
  //   },
  //   {
  //     field: 'actions',
  //     headerName: 'Действия',
  //     flex: 1,
  //     minWidth: 120,
  //     align: 'center',
  //     sortable: false,
  //     renderCell: (params) => (
  //       <Box sx={{
  //         display: 'flex',
  //         justifyContent: 'center',
  //         gap: 0.5,
  //         width: '100%'
  //       }}>
  //         <IconButton
  //           size="small"
  //           color={params.row.isBlocked ? 'success' : 'error'}
  //           onClick={() => handleOpenBlockDialog(params.row)}
  //           title={params.row.isBlocked ? 'Разблокировать' : 'Заблокировать'}
  //         >
  //           {params.row.isBlocked ? <LockOpenIcon fontSize="small" /> : <BlockIcon fontSize="small" />}
  //         </IconButton>
  //         <IconButton
  //           size="small"
  //           color="primary"
  //           onClick={() => navigate(`/users/${params.row._id}`)}
  //           title="Детали"
  //         >
  //           <InfoIcon fontSize="small" />
  //         </IconButton>
  //         <IconButton
  //           size="small"
  //           color="error"
  //           onClick={() => handleOpenDeleteDialog(params.row)}
  //           title="Удалить"
  //         >
  //           <DeleteIcon fontSize="small" />
  //         </IconButton>
  //       </Box>
  //     )
  //   }
  // ];

  // return (
  //   <Box>
  //     <Typography variant="h4" component="h1" gutterBottom sx={{ mb: 4, fontWeight: 'bold' }}>
  //       Пользователи
  //     </Typography>

  //     {/* Поиск */}
  //     <Paper
  //       elevation={0}
  //       sx={{
  //         p: 2,
  //         mb: 3,
  //         display: 'flex',
  //         alignItems: 'center',
  //         borderRadius: 2,
  //         border: '1px solid',
  //         borderColor: 'divider'
  //       }}
  //     >
  //       <TextField
  //         fullWidth
  //         placeholder="Поиск по email или телефону"
  //         value={search}
  //         onChange={(e) => setSearch(e.target.value)}
  //         onKeyDown={handleSearchKeyDown}
  //         InputProps={{
  //           startAdornment: (
  //             <InputAdornment position="start">
  //               <SearchIcon color="action" />
  //             </InputAdornment>
  //           ),
  //           endAdornment: search && (
  //             <InputAdornment position="end">
  //               <IconButton onClick={handleClearSearch} edge="end">
  //                 <ClearIcon />
  //               </IconButton>
  //             </InputAdornment>
  //           )
  //         }}
  //         sx={{ mr: 2 }}
  //       />
  //       <Button
  //         variant="contained"
  //         onClick={handleSearch}
  //         disabled={loading}
  //       >
  //         Поиск
  //       </Button>
  //     </Paper>

  //     {/* Таблица пользователей */}
  //     <Paper
  //       elevation={0}
  //       sx={{
  //         height: 600,
  //         width: '100%',
  //         borderRadius: 2,
  //         border: '1px solid',
  //         borderColor: 'divider',
  //         '& .MuiDataGrid-cell:focus': {
  //           outline: 'none',
  //         }
  //       }}
  //     >
  //       <DataGrid
  //         rows={users}
  //         columns={columns}
  //         pagination
  //         pageSize={pageSize}
  //         rowsPerPageOptions={[5, 10, 25, 50]}
  //         rowCount={totalUsers}
  //         paginationMode="server"
  //         onPageChange={(newPage) => setPage(newPage)}
  //         onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
  //         loading={loading}
  //         disableSelectionOnClick
  //         getRowId={(row) => row._id}
  //         sx={{
  //           border: 'none',
  //           '& .MuiDataGrid-columnHeaders': {
  //             backgroundColor: 'action.hover',
  //           }
  //         }}
  //       />
  //     </Paper>

  //     {/* Диалог блокировки/разблокировки */}
  //     <Dialog
  //       open={dialogOpen}
  //       onClose={handleCloseDialog}
  //     >
  //       <DialogTitle>
  //         {selectedUser?.isBlocked ? 'Разблокировать пользователя?' : 'Заблокировать пользователя?'}
  //       </DialogTitle>
  //       <DialogContent>
  //         <DialogContentText>
  //           {selectedUser?.isBlocked
  //             ? 'Вы уверены, что хотите разблокировать этого пользователя? Он снова сможет использовать приложение.'
  //             : 'Вы уверены, что хотите заблокировать этого пользователя? Он не сможет использовать приложение до разблокировки.'}
  //         </DialogContentText>
  //         <Box sx={{ mt: 2 }}>
  //           <Typography variant="subtitle2">
  //             Контакт: {selectedUser?.email || selectedUser?.phone}
  //           </Typography>
  //           <Typography variant="subtitle2">
  //             Метод: {selectedUser?.authMethod === 'email' ? 'Email' : 'Телефон'}
  //           </Typography>
  //         </Box>
  //       </DialogContent>
  //       <DialogActions>
  //         <Button onClick={handleCloseDialog} disabled={actionLoading}>
  //           Отмена
  //         </Button>
  //         <Button
  //           onClick={handleToggleBlock}
  //           color={selectedUser?.isBlocked ? 'success' : 'error'}
  //           variant="contained"
  //           disabled={actionLoading}
  //           startIcon={actionLoading && <CircularProgress size={20} color="inherit" />}
  //         >
  //           {selectedUser?.isBlocked ? 'Разблокировать' : 'Заблокировать'}
  //         </Button>
  //       </DialogActions>
  //     </Dialog>

  //     {/* Диалог удаления */}
  //     <Dialog
  //       open={deleteDialogOpen}
  //       onClose={handleCloseDeleteDialog}
  //     >
  //       <DialogTitle>
  //         Удалить пользователя?
  //       </DialogTitle>
  //       <DialogContent>
  //         <DialogContentText>
  //           Вы уверены, что хотите полностью удалить этого пользователя? Это действие невозможно отменить.
  //         </DialogContentText>
  //         <Box sx={{ mt: 2 }}>
  //           <Typography variant="subtitle2">
  //             Контакт: {userToDelete?.email || userToDelete?.phone}
  //           </Typography>
  //           <Typography variant="subtitle2">
  //             Метод: {userToDelete?.authMethod === 'email' ? 'Email' : 'Телефон'}
  //           </Typography>
  //         </Box>
  //       </DialogContent>
  //       <DialogActions>
  //         <Button onClick={handleCloseDeleteDialog} disabled={deleteLoading}>
  //           Отмена
  //         </Button>
  //         <Button
  //           onClick={handleDeleteUser}
  //           color="error"
  //           variant="contained"
  //           disabled={deleteLoading}
  //           startIcon={deleteLoading && <CircularProgress size={20} color="inherit" />}
  //         >
  //           Удалить
  //         </Button>
  //       </DialogActions>
  //     </Dialog>

  //     {/* Уведомление */}
  //     <Snackbar
  //       open={snackbar.open}
  //       autoHideDuration={6000}
  //       onClose={handleCloseSnackbar}
  //       anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
  //     >
  //       <Alert
  //         onClose={handleCloseSnackbar}
  //         severity={snackbar.severity}
  //         sx={{ width: '100%' }}
  //       >
  //         {snackbar.message}
  //       </Alert>
  //     </Snackbar>
  //   </Box>
  // );

  const columns = [
    {
      name: "contact",
      label: "Контакт",
      options: {
        filter: false,
        sort: true,
      }
    },
    {
      name: "name",
      label: "Имя",
      options: {
        filter: false,
        sort: true,
      }
    },
    {
      name: "city",
      label: "Город",
      options: {
        filter: true,
        sort: true,
      }
    },
    {
      name: "method",
      label: "Метод",
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => (
          <Chip
            label={value}
            color={value === "Email" ? 'info' : 'warning'}
            size="small"
            variant="outlined"
          />
        )
      }
    },
    {
      name: "status",
      label: "Статус",
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => (
          <Chip
            icon={value === 'Заблокирован' ? <BlockIcon /> : <CheckCircleIcon />}
            color={value === 'Заблокирован' ? 'error' : 'success'}
            label={value}
            size="small"
            variant="outlined"
          />
        )
      }
    },
    {
      name: "lastLogin",
      label: "Входил",
      options: {
        filter: false,
        sort: true,
        setCellHeaderProps: () => ({ style: { minWidth: "10%", width: "10%" } }),
        setCellProps: () => ({ style: { minWidth: "10%", width: "10%" } }),
      }
    },
    {
      name: "createdAt",
      label: "Создан",
      options: {
        filter: false,
        sort: true,
        setCellHeaderProps: () => ({ style: { minWidth: "10%", width: "10%" } }),
        setCellProps: () => ({ style: { minWidth: "10%", width: "10%" } }),
      }
    },
  ];

  const data = users.map((item, index) => ({
    index,
    name: item.name,
    city: item.city,
    contact: item.authMethod === "email" ? `📩 ${item.email}` : `📞 ${item.phone}`,
    method: item.authMethod === "email" ? 'Email' : 'Телефон',
    status: item.isBlocked ? 'Заблокирован' : 'Активен',
    createdAt: item.createdAt?.split('T')[0],
    lastLogin: item.lastLogin?.split('T')[0],
  }));

  // console.log(users, data);

  const options = {
    storageKey: 'users-table',
    rowsPerPageOptions: [10, 25, 50, 100],
    filterType: 'multiselect',
    downloadOptions: {
      filename: `Пользователи.${new Date().toISOString().split('T')[0]}.csv`,
      filterOptions: {
        useDisplayedColumnsOnly: true,
        useDisplayedRowsOnly: true,
      }
    },
    textLabels: {
      body: {
        noMatch: loading ? (<Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>) : ('Подходящих записей не найдено'),
        toolTip: "Сортировка",
        columnHeaderTooltip: (column) => `Сортировка для ${column.label}`
      },
      pagination: {
        next: "Следующая страница",
        previous: "Предыдущая страница",
        rowsPerPage: "Строк на странице:",
        displayRows: "из",
      },
      toolbar: {
        search: "Поиск",
        downloadCsv: "Скачать CSV",
        print: "Печать",
        viewColumns: "Просмотр столбцов",
        filterTable: "Фильтр таблицы",
      },
      filter: {
        all: "Все",
        title: "ФИЛЬТРЫ",
        reset: "СБРОС",
      },
      viewColumns: {
        title: "Показать столбцы",
        titleAria: "Показать/скрыть столбцы таблицы",
      },
      selectedRows: {
        text: "выбрано",
        delete: "Удалить",
        deleteAria: "Удалить выбранные строки",
      },
    },
    customToolbarSelect: (selectedRows, displayData, setSelectedRows) => (
      <div style={{ display: 'flex', flexDirection: 'row' }}>
        <div style={{ marginRight: '0.5rem' }}>
          <Tooltip title={"Заблокировать"}>
            <IconButton
              onClick={() => {
                const selectedIndices = selectedRows.data.map(row => row.dataIndex);
                const selectedData = selectedIndices.map(index => data[index]);
                console.log("Processing custom action for:", selectedData);
                setSelectedRows([]);
              }}
            ><BlockIcon />
            </IconButton>
          </Tooltip>
        </div>
        <div style={{ marginRight: '0.5rem' }}>
          <Tooltip title={"Удалить"}>
            <IconButton
              onClick={() => {
                const selectedIndices = selectedRows.data.map(row => row.dataIndex);
                const selectedData = selectedIndices.map(index => data[index]);
                console.log("Processing custom action for:", selectedData);
                setSelectedRows([]);
              }}
            ><DeleteIcon />
            </IconButton>
          </Tooltip>
        </div>
      </div>)
  };

  // const options = {
  //   serverSide: true,
  //   onTableChange: (action, tableState) => {
  //     this.xhrRequest('my.api.com/tableData', result => {
  //       this.setState({ data: result });
  //     });
  //   }
  // };

  const getMuiTheme = () => createTheme({
    components: {
      MUIDataTableToolbar: {
        styleOverrides: {
          filterPaper: {
            width: '25%',
            minWidth: '20vw',
          },
        },
      },
      MuiGrid: {
        styleOverrides: {
          root: {
            flexBasis: '100% !important',
            maxWidth: '100% !important',
            width: '100%',
            marginLeft: '0',
          },
          item: {
            paddingLeft: '0 !important',
            paddingTop: '1rem !important',
          }
        }
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: '1rem',
            minWidth: '20vw !important',
            padding: '0.5rem !important',
          }
        }
      },
      MUIDataTableViewCol: {
        styleOverrides: {
          root: {
            padding: '1.5rem !important',
          }
        }
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            padding: '0.5rem !important',
          }
        }
      },
    },
  });

  return (
    <ThemeProvider theme={getMuiTheme()}>
      <MUIDataTable
        title={"Пользователи"}
        data={data}
        columns={columns}
        options={options}
      />
    </ThemeProvider>
  );
};

export default UsersPage; 